import { Component, inject, OnInit, OnDestroy, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ScoringRealtimeService, ScorecardActivityData, TournamentActivityData, RoundActivityData, RoundOfficialActivityData } from '../../services/signalr/scoring-realtime.service';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { Subscription } from 'rxjs';

// Type for combined round events (both Active and Official)
type CombinedRoundEvent = (RoundActivityData & { category: 'active' }) | (RoundOfficialActivityData & { category: 'official' });

@Component({
  selector: 'app-real-time-events',
  standalone: true,
  imports: [CommonModule, DatePipe],
  templateUrl: './real-time-events.component.html',
  styleUrls: ['./real-time-events.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RealTimeEventsComponent implements OnInit, OnDestroy {
  protected readonly scoringRealtimeService = inject(ScoringRealtimeService);
  private readonly loggingService = inject(LoggingService);

  private scoringEventsSubscription?: Subscription;
  private tournamentEventsSubscription?: Subscription;
  private roundEventsSubscription?: Subscription;
  private roundOfficialEventsSubscription?: Subscription;

  // UI state (component-level)
  expandedEventIds = signal<Set<string>>(new Set());
  activeTab = signal<'scoring' | 'tournament' | 'round'>('scoring');

  // Service-level state (persistent across component lifecycle)
  readonly recentScoringEvents = this.scoringRealtimeService.eventHistory;
  readonly totalEventsReceived = this.scoringRealtimeService.totalEventsReceived;
  readonly lastEventReceived = this.scoringRealtimeService.lastEventReceived;

  // Tournament events state
  readonly recentTournamentEvents = signal<TournamentActivityData[]>([]);
  readonly totalTournamentEventsReceived = signal<number>(0);
  readonly lastTournamentEventReceived = signal<Date | null>(null);

  // Round events state
  readonly recentRoundEvents = signal<RoundActivityData[]>([]);
  readonly totalRoundEventsReceived = signal<number>(0);
  readonly lastRoundEventReceived = signal<Date | null>(null);

  // Round Official events state
  readonly recentRoundOfficialEvents = signal<RoundOfficialActivityData[]>([]);
  readonly totalRoundOfficialEventsReceived = signal<number>(0);
  readonly lastRoundOfficialEventReceived = signal<Date | null>(null);

  // Computed signals for reactive UI updates
  readonly isConnected = computed(() => this.scoringRealtimeService.isConnected());
  readonly scoringEventCount = computed(() => this.scoringRealtimeService.eventCount());
  readonly tournamentEventCount = computed(() => this.recentTournamentEvents().length);
  readonly roundEventCount = computed(() => this.recentRoundEvents().length);
  readonly roundOfficialEventCount = computed(() => this.recentRoundOfficialEvents().length);
  readonly totalEventCount = computed(() => this.scoringEventCount() + this.tournamentEventCount() + this.roundEventCount() + this.roundOfficialEventCount());

  // Combined Round events (both Active and Official) in chronological order
  readonly combinedRoundEvents = computed(() => {
    const activeEvents = this.recentRoundEvents().map(event => ({ ...event, category: 'active' as const }));
    const officialEvents = this.recentRoundOfficialEvents().map(event => ({ ...event, category: 'official' as const }));
    
    // Combine and sort by timestamp (newest first)
    const combined = [...activeEvents, ...officialEvents];
    return combined.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  });
  readonly hasEvents = computed(() => this.totalEventCount() > 0);
  readonly isListening = computed(() => this.scoringRealtimeService.isListeningComputed());
  readonly transportType = computed(() => this.scoringRealtimeService.transportType());
  readonly transportOptimality = computed(() => this.scoringRealtimeService.transportOptimality());

  // Computed signal to find the most recent event across all event types
  readonly lastEventReceivedOverall = computed(() => {
    const scoringLast = this.lastEventReceived();
    const tournamentLast = this.lastTournamentEventReceived();
    const roundLast = this.lastRoundEventReceived();
    const roundOfficialLast = this.lastRoundOfficialEventReceived();

    // Filter out null values and find the most recent
    const dates = [scoringLast, tournamentLast, roundLast, roundOfficialLast].filter(date => date !== null) as Date[];

    if (dates.length === 0) {
      return null;
    }

    // Return the most recent date
    return dates.reduce((latest, current) =>
      current > latest ? current : latest
    );
  });

  async ngOnInit() {
    // Ensure we receive ALL SignalR events by unsubscribing from player-specific mode
    await this.ensureAllEventsSubscription();

    this.startListeningToScoringEvents();
    this.startListeningToTournamentEvents();
    this.startListeningToRoundEvents();
    this.startListeningToRoundOfficialEvents();
    this.loggingService.debug('RealTimeEventsComponent', 'Notifications component initialized - monitoring ALL real-time events');
    this.loggingService.debug('RealTimeEventsComponent', `Component initialized with ${this.totalEventCount()} existing events from service`);
  }

  ngOnDestroy() {
    this.stopListeningToScoringEvents();
    this.stopListeningToTournamentEvents();
    this.stopListeningToRoundEvents();
    this.stopListeningToRoundOfficialEvents();
    this.loggingService.debug('RealTimeEventsComponent', 'Real-time Events component destroyed');
    this.loggingService.debug('RealTimeEventsComponent', 'Note: SignalR connection remains active for other components to use group-specific subscriptions');
  }

  /**
   * Start listening to real-time scoring events
   */
  private startListeningToScoringEvents(): void {
    this.loggingService.debug('RealTimeEventsComponent', 'Starting to listen for real-time scoring events...');

    this.scoringEventsSubscription = this.scoringRealtimeService.scorecardActivity
      .subscribe({
        next: (event: ScorecardActivityData) => {
          this.loggingService.debug('RealTimeEventsComponent', 'Received scoring event for notification:', event);
          this.handleScorecardEvent(event);
        },
        error: (error) => {
          this.loggingService.error('RealTimeEventsComponent', 'Error in scoring events subscription:', error);
        },
        complete: () => {
          this.loggingService.debug('RealTimeEventsComponent', 'Scoring events subscription completed');
        }
      });

    this.loggingService.debug('RealTimeEventsComponent', 'Subscribed to real-time scoring events');
  }

  /**
   * Stop listening to real-time scoring events
   */
  private stopListeningToScoringEvents(): void {
    if (this.scoringEventsSubscription) {
      this.scoringEventsSubscription.unsubscribe();
      this.scoringEventsSubscription = undefined;
      this.loggingService.debug('RealTimeEventsComponent', 'Unsubscribed from real-time scoring events');
    }
  }

  /**
   * Handle incoming scoring events
   */
  private handleScorecardEvent(event: ScorecardActivityData): void {
    this.loggingService.debug('RealTimeEventsComponent', 'Processing scoring event:', {
      ...event,
      tournamentId: event.tournamentId,
      roundId: event.roundId,
      playerId: event.playerId
    });
    // Events are now automatically stored in the service
    // The component just needs to handle the subscription
  }

  /**
   * Start listening to real-time tournament events
   */
  private startListeningToTournamentEvents(): void {
    this.tournamentEventsSubscription = this.scoringRealtimeService.tournamentActivity.subscribe({
      next: (event: TournamentActivityData) => {
        this.handleTournamentEvent(event);
      },
      error: (error) => {
        this.loggingService.error('RealTimeEventsComponent', 'Error in tournament events subscription:', error);
      }
    });
    this.loggingService.debug('RealTimeEventsComponent', 'Started listening to real-time tournament events');
  }

  /**
   * Stop listening to real-time tournament events
   */
  private stopListeningToTournamentEvents(): void {
    if (this.tournamentEventsSubscription) {
      this.tournamentEventsSubscription.unsubscribe();
      this.tournamentEventsSubscription = undefined;
      this.loggingService.debug('RealTimeEventsComponent', 'Unsubscribed from real-time tournament events');
    }
  }

  /**
   * Handle incoming tournament events
   */
  private handleTournamentEvent(event: TournamentActivityData): void {
    this.loggingService.debug('RealTimeEventsComponent', 'Processing tournament event:', event);

    // Add to recent events (keep last 50)
    const currentEvents = this.recentTournamentEvents();
    const updatedEvents = [event, ...currentEvents].slice(0, 50);
    this.recentTournamentEvents.set(updatedEvents);

    // Update counters
    this.totalTournamentEventsReceived.update(count => count + 1);
    this.lastTournamentEventReceived.set(event.timestamp);

    this.loggingService.info('RealTimeEventsComponent', `Tournament event processed: ${event.type} for tournament ${event.tournamentId}`);
  }

  /**
   * Start listening to real-time round events
   */
  private startListeningToRoundEvents(): void {
    this.roundEventsSubscription = this.scoringRealtimeService.roundActivity.subscribe({
      next: (event: RoundActivityData) => {
        this.handleRoundEvent(event);
      },
      error: (error) => {
        this.loggingService.error('RealTimeEventsComponent', 'Error in round events subscription:', error);
      }
    });
    this.loggingService.debug('RealTimeEventsComponent', 'Started listening to real-time round events');
  }

  /**
   * Stop listening to real-time round events
   */
  private stopListeningToRoundEvents(): void {
    if (this.roundEventsSubscription) {
      this.roundEventsSubscription.unsubscribe();
      this.roundEventsSubscription = undefined;
      this.loggingService.debug('RealTimeEventsComponent', 'Unsubscribed from real-time round events');
    }
  }

  /**
   * Handle incoming round events
   */
  private handleRoundEvent(event: RoundActivityData): void {
    this.loggingService.debug('RealTimeEventsComponent', 'Processing round event:', event);

    // Add to recent events (keep last 50)
    const currentEvents = this.recentRoundEvents();
    const updatedEvents = [event, ...currentEvents].slice(0, 50);
    this.recentRoundEvents.set(updatedEvents);

    // Update counters
    this.totalRoundEventsReceived.update(count => count + 1);
    this.lastRoundEventReceived.set(event.timestamp);

    this.loggingService.info('RealTimeEventsComponent', `Round event processed: ${event.type} for round ${event.roundId}`);
  }

  /**
   * Start listening to real-time round official events
   */
  private startListeningToRoundOfficialEvents(): void {
    this.roundOfficialEventsSubscription = this.scoringRealtimeService.roundOfficialActivity.subscribe({
      next: (event: RoundOfficialActivityData) => {
        this.handleRoundOfficialEvent(event);
      },
      error: (error) => {
        this.loggingService.error('RealTimeEventsComponent', 'Error in round official events subscription:', error);
      }
    });
    this.loggingService.debug('RealTimeEventsComponent', 'Started listening to real-time round official events');
  }

  /**
   * Stop listening to real-time round official events
   */
  private stopListeningToRoundOfficialEvents(): void {
    if (this.roundOfficialEventsSubscription) {
      this.roundOfficialEventsSubscription.unsubscribe();
      this.roundOfficialEventsSubscription = undefined;
      this.loggingService.debug('RealTimeEventsComponent', 'Unsubscribed from real-time round official events');
    }
  }

  /**
   * Handle incoming round official events
   */
  private handleRoundOfficialEvent(event: RoundOfficialActivityData): void {
    this.loggingService.debug('RealTimeEventsComponent', 'Processing round official event:', event);

    // Add to recent events (keep last 50)
    const currentEvents = this.recentRoundOfficialEvents();
    const updatedEvents = [event, ...currentEvents].slice(0, 50);
    this.recentRoundOfficialEvents.set(updatedEvents);

    // Update counters
    this.totalRoundOfficialEventsReceived.update(count => count + 1);
    this.lastRoundOfficialEventReceived.set(event.timestamp);

    this.loggingService.info('RealTimeEventsComponent', `Round official event processed: ${event.type} for round ${event.roundId}`);
  }

  /**
   * Ensure this component receives ALL SignalR events (not just player-specific ones)
   */
  private async ensureAllEventsSubscription(): Promise<void> {
    try {
      // Unsubscribe from player-specific mode to ensure we receive ALL events
      await this.scoringRealtimeService.unsubscribeFromAllPlayerEvents();
      this.loggingService.info('RealTimeEventsComponent', 'Unsubscribed from player-specific mode - now receiving ALL SignalR events');
      this.loggingService.info('RealTimeEventsComponent', 'Real-time Events page is now monitoring ALL SignalR events for comprehensive system monitoring');
    } catch (error) {
      this.loggingService.warn('RealTimeEventsComponent', 'Could not unsubscribe from player events (may not have been subscribed):', error);
      // This is expected if we weren't subscribed to player events, so we continue
    }
  }

  setActiveTab(tab: 'scoring' | 'tournament' | 'round'): void {
    this.activeTab.set(tab);
  }

  /**
   * Start listening to events
   */
  async startListening(): Promise<void> {
    try {
      await this.scoringRealtimeService.startListening();
      this.loggingService.debug('RealTimeEventsComponent', 'Started listening to SignalR events');
    } catch (error) {
      this.loggingService.error('RealTimeEventsComponent', 'Error starting to listen:', error);
    }
  }

  /**
   * Stop listening to events
   */
  stopListening(): void {
    try {
      this.scoringRealtimeService.stopListening();
      this.loggingService.debug('RealTimeEventsComponent', 'Stopped listening to SignalR events');
    } catch (error) {
      this.loggingService.error('RealTimeEventsComponent', 'Error stopping listening:', error);
    }
  }

  /**
   * Attempt to upgrade the transport
   */
  async attemptTransportUpgrade(): Promise<void> {
    try {
      const result = await this.scoringRealtimeService.attemptTransportUpgrade();

      if (result.success) {
        this.loggingService.info('RealTimeEventsComponent', 'Transport upgrade available:', result.message);
        alert(`Transport Upgrade Available: ${result.message}`);
      } else {
        this.loggingService.info('RealTimeEventsComponent', 'Transport upgrade not available:', result.message);
        alert(`Transport Upgrade: ${result.message}`);
      }
    } catch (error) {
      this.loggingService.error('RealTimeEventsComponent', 'Error attempting transport upgrade:', error);
      alert(`Transport upgrade failed: ${error}`);
    }
  }

  /**
   * Force reconnection for transport upgrade
   */
  async forceTransportReconnection(): Promise<void> {
    try {
      await this.scoringRealtimeService.forceTransportReconnection();
      alert('Connection reset. Please refresh the page to reconnect with optimal transport.');
    } catch (error) {
      this.loggingService.error('RealTimeEventsComponent', 'Error forcing transport reconnection:', error);
      alert(`Reconnection failed: ${error}`);
    }
  }

  /**
   * Test method to trigger a test scoring event
   */
  testNotification(): void {
    this.scoringRealtimeService.testBroadcast();
  }

  /**
   * Test method to trigger a test tournament event
   */
  testTournamentNotification(): void {
    // Create a test tournament event
    const testEvent: TournamentActivityData = {
      type: 'TournamentMarkedActive',
      tournamentId: `test-tournament-${Date.now()}`,
      active: true,
      activeOnUtc: new Date(),
      timestamp: new Date()
    };

    this.handleTournamentEvent(testEvent);
    this.loggingService.info('RealTimeEventsComponent', 'Test tournament event triggered', testEvent);
  }

  /**
   * Test method to trigger a test round event
   */
  testRoundNotification(): void {
    // Create a test round event
    const testEvent: RoundActivityData = {
      type: 'RoundMarkedActive',
      roundId: `test-round-${Date.now()}`,
      active: true,
      activeOnUtc: new Date(),
      timestamp: new Date()
    };

    this.handleRoundEvent(testEvent);
    this.loggingService.info('RealTimeEventsComponent', 'Test round event triggered', testEvent);
  }

  /**
   * Clear all events (scoring, tournament, and round events)
   */
  clearAllEvents(): void {
    // Clear scoring events from service
    this.scoringRealtimeService.clearEventHistory();
    
    // Clear tournament events from component
    this.recentTournamentEvents.set([]);
    this.totalTournamentEventsReceived.set(0);
    this.lastTournamentEventReceived.set(null);
    
    // Clear round events from component
    this.recentRoundEvents.set([]);
    this.totalRoundEventsReceived.set(0);
    this.lastRoundEventReceived.set(null);
    
    // Clear expanded event IDs
    this.expandedEventIds.set(new Set());
    
    this.loggingService.debug('RealTimeEventsComponent', 'All events cleared (scoring, tournament, and round events)');
  }

  /**
   * Copy text to clipboard
   */
  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.loggingService.debug('RealTimeEventsComponent', 'Copied to clipboard:', text);
    } catch (error) {
      this.loggingService.error('RealTimeEventsComponent', 'Failed to copy to clipboard:', error);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }

  /**
   * Toggle JSON expansion for an event
   */
  toggleEventJson(event: ScorecardActivityData): void {
    const eventId = this.getEventId(event);
    const expandedIds = new Set(this.expandedEventIds());

    if (expandedIds.has(eventId)) {
      expandedIds.delete(eventId);
    } else {
      expandedIds.add(eventId);
    }

    this.expandedEventIds.set(expandedIds);
  }

  /**
   * Check if event JSON is expanded
   */
  isEventJsonExpanded(event: ScorecardActivityData): boolean {
    const eventId = this.getEventId(event);
    return this.expandedEventIds().has(eventId);
  }

  /**
   * Get formatted JSON for an event
   */
  getEventJson(event: ScorecardActivityData): string {
    return JSON.stringify(event, null, 2);
  }

  /**
   * Get unique ID for an event
   */
  private getEventId(event: ScorecardActivityData): string {
    return `${event.scorecardId}-${event.timestamp.getTime()}`;
  }

  /**
   * Copy event JSON to clipboard
   */
  async copyEventJson(event: ScorecardActivityData): Promise<void> {
    const json = this.getEventJson(event);
    await this.copyToClipboard(json);
  }

  /**
   * Toggle tournament event JSON display
   */
  toggleTournamentEventJson(event: TournamentActivityData): void {
    const eventId = this.getTournamentEventId(event);
    const currentExpanded = this.expandedEventIds();
    const newExpanded = new Set(currentExpanded);

    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }

    this.expandedEventIds.set(newExpanded);
    this.loggingService.debug('RealTimeEventsComponent', `Toggled tournament event JSON for ${eventId}`);
  }

  /**
   * Check if tournament event JSON is expanded
   */
  isTournamentEventJsonExpanded(event: TournamentActivityData): boolean {
    const eventId = this.getTournamentEventId(event);
    return this.expandedEventIds().has(eventId);
  }

  /**
   * Get tournament event JSON string
   */
  getTournamentEventJson(event: TournamentActivityData): string {
    return JSON.stringify(event, null, 2);
  }

  /**
   * Get unique tournament event ID for tracking
   */
  private getTournamentEventId(event: TournamentActivityData): string {
    return `tournament-${event.tournamentId}-${event.timestamp.getTime()}`;
  }

  /**
   * Copy tournament event JSON to clipboard
   */
  async copyTournamentEventJson(event: TournamentActivityData): Promise<void> {
    const json = this.getTournamentEventJson(event);
    await this.copyToClipboard(json);
  }

  /**
   * Toggle round event JSON display
   */
  toggleRoundEventJson(event: CombinedRoundEvent): void {
    const eventId = this.getRoundEventId(event);
    const currentExpanded = this.expandedEventIds();
    const newExpanded = new Set(currentExpanded);

    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }

    this.expandedEventIds.set(newExpanded);
    this.loggingService.debug('RealTimeEventsComponent', `Toggled round event JSON for ${eventId}`);
  }

  /**
   * Check if round event JSON is expanded
   */
  isRoundEventJsonExpanded(event: CombinedRoundEvent): boolean {
    const eventId = this.getRoundEventId(event);
    return this.expandedEventIds().has(eventId);
  }

  /**
   * Get round event JSON string
   */
  getRoundEventJson(event: CombinedRoundEvent): string {
    return JSON.stringify(event, null, 2);
  }

  /**
   * Get unique round event ID for tracking
   */
  private getRoundEventId(event: CombinedRoundEvent): string {
    return `round-${event.roundId}-${event.timestamp.getTime()}`;
  }

  /**
   * Copy round event JSON to clipboard
   */
  async copyRoundEventJson(event: CombinedRoundEvent): Promise<void> {
    const json = this.getRoundEventJson(event);
    await this.copyToClipboard(json);
  }
}
